import React, { useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { EditActionIcon } from "@components/common/Icons/ActionIcons";
import WarningIcon from "@mui/icons-material/Warning";
import InputField from "@components/common/InputField";
import EquipmentDropdown from "../../EquipmentDropdown";
import { useTranslation } from "react-i18next";
import { equipmentCountSchema } from "../../../schema/create.lab.schema";

interface EquipmentSelectorProps {
	selectedEquipmentId: string | null;
	equipmentCount: number;
	editingEquipmentIndex: number | null;
	visibleEquipmentOptions: Array<{ value: string; label: string }>;
	hasMoreOptions: boolean;
	isEquipmentSearchLoading: boolean;
	isFetchingNextPage: boolean;
	isSubmitting: boolean;
	canAddEquipment: boolean;
	canConfirmAction: boolean;
	onEquipmentSelect: (value: string) => void;
	onCountChange: (count: number) => void;
	onAddEquipment: () => void;
	onSaveEdit: () => void;
	onCancelEdit: () => void;
	onLoadMoreOptions: () => void;
	onEquipmentSearchChange?: (value: string) => void;
}

const EquipmentSelector: React.FC<EquipmentSelectorProps> = ({
	selectedEquipmentId,
	equipmentCount,
	editingEquipmentIndex,
	visibleEquipmentOptions,
	hasMoreOptions,
	isEquipmentSearchLoading,
	isFetchingNextPage,
	isSubmitting,
	canAddEquipment,
	canConfirmAction,
	onEquipmentSelect,
	onCountChange,
	onAddEquipment,
	onSaveEdit,
	onCancelEdit,
	onLoadMoreOptions,
	onEquipmentSearchChange,
}) => {
	const { t } = useTranslation();
	const [inputValue, setInputValue] = useState<string>(equipmentCount.toString());
	const [validationError, setValidationError] = useState<string>("");

	// Update local input value when equipmentCount prop changes
	React.useEffect(() => {
		setInputValue(equipmentCount.toString());
	}, [equipmentCount]);

	if (!canAddEquipment) return null;

	return (
		<div
			className={`border rounded-lg p-4 transition-all duration-200 ${
				editingEquipmentIndex !== null ? "border-orange-300 bg-orange-50" : "border-gray-200 bg-white"
			}`}>
			{editingEquipmentIndex !== null && (
				<div className="flex items-center gap-2 mb-3 p-2 bg-orange-100 rounded-lg">
					<EditActionIcon fontSize="small" />
					<span className="text-sm font-medium text-orange-800">
						{t("feature.courseBuilder.labs.createLab.editingEquipment")}
					</span>
				</div>
			)}

			<div className="flex flex-wrap items-end gap-4">
				{/* Equipment Selection */}
				<div className="flex-1 min-w-0">
					<EquipmentDropdown
						label="Select equipment"
						selected={selectedEquipmentId || ""}
						setSelected={(value: string) => {
							if (value === "__load_more__") {
								onLoadMoreOptions();
							} else {
								onEquipmentSelect(value);
							}
						}}
						disabled={isSubmitting}
						options={visibleEquipmentOptions}
						hasMoreOptions={hasMoreOptions && !isEquipmentSearchLoading}
						isLoading={isEquipmentSearchLoading}
						isLoadingMore={isFetchingNextPage}
						onScrollEnd={onLoadMoreOptions}
						onSearchChange={onEquipmentSearchChange}
					/>
				</div>

				{/* Count Input */}
				<div className="w-26 relative">
					<div className="flex items-center gap-1">
						<InputField
							id="equipment-count"
							label={t("feature.courseBuilder.labs.createLab.requiredCount")}
							type="number"
							placeholder="Count"
							value={inputValue}
							onChange={e => {
								const newValue = e.target.value;
								// Prevent typing numbers greater than 9999
								if (newValue !== "" && !isNaN(Number(newValue))) {
									const numValue = parseInt(newValue);
									if (numValue > 9999) {
										// Don't allow typing numbers greater than 9999
										return;
									}
								}
								setInputValue(newValue);
								setValidationError("");

								// Parse and validate the input
								if (newValue === "" || newValue === "0") {
									onCountChange(1);
									return;
								}

								const numValue = parseInt(newValue);
								if (!isNaN(numValue)) {
									// Validate using Zod schema
									const validation = equipmentCountSchema.safeParse(numValue);
									if (validation.success) {
										onCountChange(numValue);
									} else {
										// Don't update the count, but show validation error
										setValidationError(validation.error.errors[0]?.message || "Invalid count");
									}
								}
							}}
							onBlur={() => {
								// On blur, ensure we have a valid value
								const numValue = parseInt(inputValue);
								if (isNaN(numValue) || numValue < 1) {
									setInputValue("1");
									onCountChange(1);
									setValidationError("");
								} else if (numValue > 9999) {
									setInputValue("9999");
									onCountChange(9999);
									setValidationError("");
								}
							}}
							disabled={isSubmitting || !selectedEquipmentId}
							required
							error={!!validationError}
							inputProps={{
								min: 1,
								max: 9999,
							}}
						/>
						{validationError && (
							<Tooltip title={validationError} arrow placement="top">
								<WarningIcon className="text-red-500 mt-6" fontSize="small" />
							</Tooltip>
						)}
					</div>
				</div>

				{/* Add/Save Button - Round Icon */}
				<IconButton
					onClick={editingEquipmentIndex !== null ? onSaveEdit : onAddEquipment}
					disabled={!canConfirmAction || isSubmitting}
					sx={{
						marginBottom: "5px",
						backgroundColor: editingEquipmentIndex !== null ? "#34d399" : "green",
						color: "white",
						boxShadow:
							editingEquipmentIndex !== null
								? "0 2px 8px rgba(52, 211, 153, 0.3)"
								: "0 2px 8px rgba(139, 250, 185, 0.3)",
						"&:hover": {
							backgroundColor: editingEquipmentIndex !== null ? "#10b981" : "#8beb92",
							boxShadow:
								editingEquipmentIndex !== null
									? "0 4px 12px rgba(52, 211, 153, 0.4)"
									: "0 4px 12px rgba(139, 250, 144, 0.4)",
						},
						"&.Mui-disabled": {
							backgroundColor: "#d1d5db",
							color: "#6b7280",
							boxShadow: "none",
						},
						transition: "all 0.2s ease-in-out",
						animation: editingEquipmentIndex === null ? "gentlePulse 2s ease-in-out infinite" : "none",
						"@keyframes gentlePulse": {
							"0%": {
								boxShadow: "0 2px 8px rgba(167, 139, 250, 0.3)",
							},
							"50%": {
								boxShadow: "0 2px 12px rgba(167, 139, 250, 0.5)",
							},
							"100%": {
								boxShadow: "0 2px 8px rgba(167, 139, 250, 0.3)",
							},
						},
					}}>
					{editingEquipmentIndex !== null ? <CheckIcon fontSize="small" /> : <AddIcon fontSize="small" />}
				</IconButton>

				{/* Cancel Edit Button */}
				{editingEquipmentIndex !== null && (
					<IconButton
						onClick={onCancelEdit}
						disabled={isSubmitting}
						sx={{
							marginBottom: "5px",
							backgroundColor: "#fbbf24",
							color: "white",
							boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
							"&:hover": {
								backgroundColor: "#f59e0b",
								transform: "scale(1.05)",
								boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
							},
							"&.Mui-disabled": {
								backgroundColor: "#d1d5db",
								color: "#6b7280",
								boxShadow: "none",
							},
							transition: "all 0.2s ease-in-out",
						}}>
						<CloseIcon fontSize="small" />
					</IconButton>
				)}
			</div>
		</div>
	);
};

export default EquipmentSelector;
